import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { clsx } from 'clsx';
import { interpolateTemplate } from '../LookupField/utils';
import { Loading } from './components/loading';
import { useMasterDocumentTranslation } from './i18n';
import type { Fetcher } from '../LookupField/services/api.types';
import type { LookupFieldConfig } from './MasterDocument.types';
// La MISMA caja que pinta `ReadonlyField`, no una copia: la que vivía aquí se
// quedó sin el `h-10` del canon y dejaba los campos vacíos en 12px de alto.
import { readonlyBase } from './fieldStyles';


// ─── Caché de resolución por (entity, valueField, id) ──────
//
// Evita repetir el fetch-by-id cuando el mismo id aparece en varios campos o
// filas de un mismo documento. Es el mismo costo que ya paga el modo edición;
// la caché impide amplificarlo. Guardamos la *promesa* para deduplicar también
// las consultas en vuelo (dos campos con el mismo id disparan un solo fetch).

type ResolveCache = Map<string, Promise<Record<string, unknown> | null>>;

const LookupResolveCacheContext = createContext<ResolveCache | null>(null);

/**
 * Provee una caché de resolución de lookups compartida por todo el árbol del
 * documento. Montado por `MasterDocumentProvider`, garantiza un único fetch por
 * (entity, id) distinto dentro de un mismo documento. Si no hay provider, cada
 * `LookupReadonlyValue` cae a una caché local propia (sigue deduplicando sus
 * re-renders, sin fugar estado entre instancias).
 */
export function LookupResolveCacheProvider({ children }: { children: ReactNode }) {
  const cacheRef = useRef<ResolveCache | null>(null);
  if (!cacheRef.current) cacheRef.current = new Map();
  return (
    <LookupResolveCacheContext.Provider value={cacheRef.current}>
      {children}
    </LookupResolveCacheContext.Provider>
  );
}

/** Deriva los campos de etiqueta del displayTemplate (o usa displayFields). */
function labelFieldsOf(cfg: LookupFieldConfig): string[] {
  return cfg.displayTemplate
    ? [...cfg.displayTemplate.matchAll(/\{(\w+)\}/g)].map((m) => m[1])
    : cfg.displayFields;
}

/** Construye la etiqueta "código - nombre" idéntica a la del modo edición. */
function computeLabel(record: Record<string, unknown>, cfg: LookupFieldConfig): string {
  if (cfg.displayTemplate) {
    const out = interpolateTemplate(cfg.displayTemplate, record).trim();
    if (out) return out;
  }
  return cfg.displayFields
    .map((f) => record[f])
    .filter((v) => v != null && v !== '')
    .map(String)
    .join(' - ');
}

/**
 * Resuelve un id a su registro vía fetch-by-id, con caché compartida.
 *
 * Devuelve:
 *  - `undefined` mientras carga (o sin id),
 *  - el registro resuelto,
 *  - `null` si el id no resolvió a ningún registro.
 */
function useResolvedLookupRecord(
  id: unknown,
  lookupConfig: LookupFieldConfig | undefined,
  fetcher: unknown,
): Record<string, unknown> | null | undefined {
  const ctxCache = useContext(LookupResolveCacheContext);
  const localCacheRef = useRef<ResolveCache | null>(null);
  if (!ctxCache && !localCacheRef.current) localCacheRef.current = new Map();
  const cache = ctxCache ?? localCacheRef.current!;

  const [record, setRecord] = useState<Record<string, unknown> | null | undefined>(undefined);

  useEffect(() => {
    const hasValue = id != null && id !== '';
    if (!hasValue || !lookupConfig || !fetcher) {
      setRecord(undefined);
      return;
    }

    let cancelled = false;
    const valueField = lookupConfig.valueField ?? 'Id';
    // El id puede ser string o number; lo normalizamos a un FilterValue primitivo
    // (los GUID llegan como string; preservamos ids numéricos tal cual).
    const filterValue: string | number =
      typeof id === 'number' ? id : String(id);
    const key = `${lookupConfig.entity}::${valueField}::${String(id)}`;

    let promise = cache.get(key);
    if (!promise) {
      // Campos a traer: el valueField (para emparejar) + los de etiqueta + los
      // displayFields originales (no se muestran de más, pero garantizan que el
      // backend genérico devuelva lo necesario para la etiqueta).
      const fields = [
        ...new Set([valueField, ...labelFieldsOf(lookupConfig), ...lookupConfig.displayFields]),
      ];
      promise = (fetcher as Fetcher)(lookupConfig.entity, {
        fields,
        filters: [[{ [valueField]: filterValue }]],
        page: 1,
        pageSize: 1,
      })
        .then((res) => res.data?.[0] ?? null)
        .catch(() => null);
      cache.set(key, promise);
    }

    setRecord(undefined);
    promise.then((rec) => {
      if (!cancelled) setRecord(rec);
    });

    return () => {
      cancelled = true;
    };
  }, [id, lookupConfig, fetcher, cache]);

  return record;
}

export interface LookupReadonlyValueProps {
  /** Id crudo (p.ej. el GUID) almacenado en el campo. */
  id: unknown;
  lookupConfig: LookupFieldConfig;
  fetcher: unknown;
  isMono?: boolean;
}

/**
 * Render solo-lectura de un campo `searcher`/`autocomplete`: resuelve el id a su
 * etiqueta ("código - nombre") con el mismo fetcher/displayTemplate que el modo
 * edición, en vez de pintar el valor crudo. Campo plano, sin interacción.
 *
 * - id vacío → render vacío (no fuerza "—").
 * - resolviendo → `Loading` inline (antes era un hueco vacío, indistinguible de
 *   "sin valor"; sigue sin destellar el id crudo).
 * - id no resuelto → cae al valor crudo (comportamiento previo, sin pérdida de info).
 */
export function LookupReadonlyValue({ id, lookupConfig, fetcher, isMono }: LookupReadonlyValueProps) {
  const record = useResolvedLookupRecord(id, lookupConfig, fetcher);
  const { t } = useMasterDocumentTranslation();

  // `record === undefined` cubre DOS casos distintos: "no hay nada que resolver"
  // (sin id, sin config o sin fetcher) y "se está resolviendo". Solo el segundo
  // lleva indicador — si no, un documento con campos vacíos giraría para siempre.
  // De ahí que se repita aquí la misma guarda que corta el efecto del hook.
  const resolving =
    record === undefined && id != null && id !== '' && !!lookupConfig && !!fetcher;

  if (resolving) {
    return (
      <div className={clsx(readonlyBase, isMono && 'tabular-nums')}>
        <Loading size="sm" layout="inline" ariaLabel={t('master_document.chrome.loadingValue')} />
      </div>
    );
  }

  let content = '';
  if (record) {
    content = computeLabel(record, lookupConfig);
  } else if (record === null && id != null && id !== '') {
    // Resolvió a "no encontrado": preservamos el valor crudo (como antes del fix).
    content = String(id);
  }

  return <div className={clsx(readonlyBase, isMono && 'tabular-nums')}>{content}</div>;
}
